import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";

const statusUi = {
  draft: { i: Clock, c: "text-[#8A8478]", l: "Draft" },
  pending: { i: Clock, c: "text-[#C5A059]", l: "Under Review" },
  verified: { i: CheckCircle2, c: "text-[#C5A059]", l: "Verified" },
  needs_changes: { i: AlertCircle, c: "text-[#C5A059]", l: "Needs Changes" },
  rejected: { i: XCircle, c: "text-red-600", l: "Rejected" },
};

export default function DashboardTalent() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/profile/me").then((r) => setProfile(r.data));
    api.get("/enquiries/mine").then((r) => setEnquiries(r.data));
  }, [user]);

  const status = user?.profile_status || "draft";
  const S = statusUi[status] || statusUi.draft;

  const respond = async (id, response) => {
    await api.post("/enquiries/respond", { enquiry_id: id, response });
    api.get("/enquiries/mine").then((r) => setEnquiries(r.data));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="sv-overline mb-3">Talent Dashboard</div>
        <h1 className="font-serif-hero text-4xl">Hello, {user?.full_name || "Talent"}.</h1>

        {user?.profile_locked && (
          <div className="mt-8 sv-card p-8 flex flex-col sm:flex-row items-start gap-4">
            <Lock className="text-[#C5A059]" />
            <div className="flex-1">
              <div className="font-serif-editorial text-xl">Membership required</div>
              <p className="text-sm text-[#8A8478] mt-1">Unlock the professional biodata builder by activating your SV Talent membership.</p>
            </div>
            <Link to="/pricing" className="sv-btn-primary" data-testid="dash-choose-plan">Choose Plan</Link>
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-4 mt-8">
          <div className="sv-card p-6"><div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Status</div><div className="flex items-center gap-2 font-serif-editorial text-xl"><S.i size={18} className={S.c} />{S.l}</div></div>
          <div className="sv-card p-6"><div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Membership</div><div className="font-serif-editorial text-xl">{user?.subscription?.plan_name || "None"}</div></div>
          <div className="sv-card p-6"><div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Enquiries</div><div className="font-serif-editorial text-xl">{enquiries.length}</div></div>
          <div className="sv-card p-6"><div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Profile</div><Link to="/onboarding" className="text-sm underline">Edit / Build</Link></div>
        </div>

        <div className="mt-10">
          <h2 className="font-serif-hero text-2xl mb-4">Casting Enquiries</h2>
          {enquiries.length === 0 ? (
            <div className="text-sm text-[#8A8478]">No enquiries yet.</div>
          ) : enquiries.map((e) => (
            <div key={e.enquiry_id} className="sv-card p-5 mb-3 flex flex-col sm:flex-row gap-4 items-start justify-between">
              <div>
                <div className="font-serif-editorial text-lg">{e.project} — {e.role}</div>
                <div className="text-xs text-[#8A8478]">{e.location} · {e.shoot_date} · From: {e.from_name}</div>
                <div className="text-sm mt-2">{e.message}</div>
                {e.response && <div className="mt-2 text-xs font-mono-label text-[#C5A059]">Your response: {e.response}</div>}
              </div>
              {!e.response && (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => respond(e.enquiry_id, "interested")} className="sv-btn-primary text-xs">Interested</button>
                  <button onClick={() => respond(e.enquiry_id, "need_more_details")} className="sv-btn-outline text-xs">Need Details</button>
                  <button onClick={() => respond(e.enquiry_id, "not_interested")} className="text-xs text-[#8A8478] underline">Not Interested</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
